import { IBase, IDays, IProductionCapacitySlot, IShop, IShopProductionCapacity, ITag, slugify } from '@app/common';

export type ProductionCapacityFormValues = IShopProductionCapacity & { tags?: Pick<ITag, '_id' | 'name'>[] };

export interface ShopFormValues extends Omit<Partial<IShop>, 'productionCapacities'> {
  productionCapacities?: ProductionCapacityFormValues[];
  // Rendues explicites bien que déjà portées par `Partial<IShop>` : ce sont
  // les quatre rubriques d'identité fiscale (NF525 R13 en-tête 10, 11, 14, 15)
  // que le formulaire doit faire traverser jusqu'au payload. Les voir ici
  // évite qu'un remaniement de `IShop` les fasse disparaître sans bruit.
  zipCode?: string;
  city?: string;
  naf?: string;
  vatNumber?: string;
}

// On ne garde que les limites des catégories sélectionnées, et on retire les cases vides
const cleanMaxProducts = (slot: IProductionCapacitySlot, tagsIds: string[]): IProductionCapacitySlot => {
  const maxProducts = Object.fromEntries(
    tagsIds
      .map((tagId) => [tagId, slot.maxProducts?.[tagId]] as const)
      .filter((entry): entry is readonly [string, number] => typeof entry[1] === 'number' && !isNaN(entry[1])),
  );

  const { maxProducts: _, ...rest } = slot;

  return Object.keys(maxProducts).length ? { ...rest, maxProducts } : rest;
};

const transformProductionCapacityToPayload = ({ tags, ...productionCapacity }: ProductionCapacityFormValues): IShopProductionCapacity => {
  const tagsIds = (tags || []).map((tag) => tag._id);

  return {
    ...productionCapacity,
    tagsIds,
    capacity: Object.fromEntries(
      Object.entries(productionCapacity.capacity || {}).map(([day, slots]) => [
        day,
        (slots || []).map((slot) => cleanMaxProducts(slot, tagsIds)),
      ]),
    ) as IShopProductionCapacity['capacity'],
  };
};

export const transformShopFormValuesToPayload = (
  values: Omit<ShopFormValues, keyof IBase>,
): Partial<IShop> => {
  return {
    ...values,
    ...(values.productionCapacities && {
      productionCapacities: values.productionCapacities.map(transformProductionCapacityToPayload),
    }),
    slug: slugify(values.publicName!),
  };
};

// TODO: On peut améliorer en affichant que les créneaux ouverts (donc pas ceux ou le restaurant est fermé)
export const getAvailableSlots = ({
  shop,
  day,
}: {
  step?: number;
  shop: IShop;
  day: IDays;
}): number[] => {
  const STEP = 15;

  const slots: number[] = [];
  const startOfDay = 0; // 00:00
  const endOfDay = 1440; // 24:00 (en minutes)

  for (let time = startOfDay; time <= endOfDay; time += STEP) {
    slots.push(time);
  }

  const shopDeliverySchedule = shop?.deliverySchedule || {};
  const shopTakeAwaySchedule = shop?.takeAwaySchedule || {};

  const takeAwayScheduleForDay = shopTakeAwaySchedule[day] || [];
  const deliveryScheduleForDay = shopDeliverySchedule[day] || [];

  const scheduleForDay = [...takeAwayScheduleForDay, ...deliveryScheduleForDay].flat();

  const minTime = Math.min(...scheduleForDay);
  const maxTime = Math.max(...scheduleForDay);

  return slots.filter((slot) => slot >= minTime && slot <= maxTime);
};

export const createDefaultCapacityForDay = (
  shop: IShop | undefined,
  day: IDays,
  productionCapacity?: IShopProductionCapacity['capacity'],
) => {
  if (!shop) {
    return [];
  }

  const slots = getAvailableSlots({ shop, day });

  return slots.slice(0, -1).map((start, index) => {
    const end = slots[index + 1];

    if (!productionCapacity || !productionCapacity[day]) {
      return {
        start,
        end,
        maxOrders: undefined,
        maxProducts: {},
      };
    }

    const slot = productionCapacity[day].find((slot) => slot.start === start && slot.end === end);

    return {
      start,
      end,
      maxOrders: slot?.maxOrders,
      maxProducts: slot?.maxProducts ?? {},
    };
  });
};
